import { NavItem } from './nav-item/nav-item';
import { adminItems } from './admin';
import { nonAdminItems } from './non-admin';
import { salesItems } from './sales';

/**
 * Roles that receive the full admin sidebar (matches login API `user.role`, case-insensitive).
 * Do not use substring checks like `includes('admin')` — e.g. "NonAdmin" would match incorrectly.
 */
const ADMIN_ROLE_NAMES = new Set(
  [
    'admin',
    'administrator',
    'super admin',
    'superadmin',
    'system administrator',
  ].map((r) => r.toLowerCase())
);

export type AppRole = 'Admin' | 'Driver' | 'Sales' | string;

/** Login / stored user object shape from API (subset). */
export interface LoginUserPayload {
  id?: string;
  email?: string;
  full_name?: string;
  role?: string | { name?: string };
  role_name?: string;
  hubs?: unknown[];
  is_superuser?: boolean;
  is_staff?: boolean;
}

/** Resolve display/API role name from login or user payloads. */
export function getAppRoleName(
  user: LoginUserPayload | { role_name?: string; role?: string | { name?: string } | null } | null | undefined
): AppRole {
  if (!user) return '';
  if (typeof user.role === 'string' && user.role.trim()) return user.role.trim() as AppRole;
  if (user.role && typeof user.role === 'object' && user.role.name) {
    return String(user.role.name).trim() as AppRole;
  }
  if (user.role_name) return String(user.role_name).trim() as AppRole;
  return '';
}

export function isAdminSidebarUser(user: LoginUserPayload | null | undefined): boolean {
  if (!user) return false;
  if (user.is_superuser === true) return true;
  const role = getAppRoleName(user).toLowerCase();
  return ADMIN_ROLE_NAMES.has(role);
}

export function isSalesSidebarUser(user: LoginUserPayload | null | undefined): boolean {
  if (!user || isAdminSidebarUser(user)) return false;
  return getAppRoleName(user).toLowerCase() === 'sales';
}

export function getSidebarNavItemsForUser(user: LoginUserPayload | null | undefined): NavItem[] {
  if (isAdminSidebarUser(user)) return adminItems;
  if (isSalesSidebarUser(user)) return salesItems;
  return nonAdminItems;
}

export function readStoredLoginUser(): LoginUserPayload | null {
  try {
    const raw = localStorage.getItem('user');
    if (!raw) return null;
    return JSON.parse(raw) as LoginUserPayload;
  } catch {
    return null;
  }
}

export function getSidebarNavItemsForStoredUser(): NavItem[] {
  return getSidebarNavItemsForUser(readStoredLoginUser());
}
